import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface NavigationEvent {
  timestamp: number;
  from: string;
  to: string;
  timeSpent: number;
  sessionId: string;
}

interface AnalyticsData {
  events: NavigationEvent[];
  sessionId: string;
  sessionStart: number;
}

const STORAGE_KEY = 'nav_analytics';
const MAX_EVENTS = 100;

// Generate a session ID for tracking user journey
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session data
const getSessionData = (): AnalyticsData => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Fall through to create new session
  }
  
  return {
    events: [],
    sessionId: generateSessionId(),
    sessionStart: Date.now(),
  };
};

export const useNavigationAnalytics = () => {
  const location = useLocation();
  const previousPath = useRef<string>(location.pathname);
  const entryTime = useRef<number>(Date.now());
  const analyticsData = useRef<AnalyticsData>(getSessionData());

  const trackNavigation = useCallback((from: string, to: string, timeSpent: number) => {
    const event: NavigationEvent = {
      timestamp: Date.now(),
      from,
      to,
      timeSpent,
      sessionId: analyticsData.current.sessionId,
    };

    analyticsData.current.events.push(event);
    
    // Keep only the most recent events
    if (analyticsData.current.events.length > MAX_EVENTS) {
      analyticsData.current.events = analyticsData.current.events.slice(-MAX_EVENTS);
    }

    // Persist to session storage
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(analyticsData.current));
    } catch {
      // Ignore storage errors
    }

    // In production, send to analytics backend
    // Example: sendToAnalyticsServer(event);
  }, []);

  const getAnalyticsSummary = useCallback(() => {
    const events = analyticsData.current.events;
    const pathCounts: Record<string, number> = {};
    const totalTimePerPath: Record<string, number> = {};

    events.forEach(event => {
      pathCounts[event.to] = (pathCounts[event.to] || 0) + 1;
      totalTimePerPath[event.to] = (totalTimePerPath[event.to] || 0) + event.timeSpent;
    });

    return {
      sessionId: analyticsData.current.sessionId,
      sessionDuration: Date.now() - analyticsData.current.sessionStart,
      totalNavigations: events.length,
      mostVisitedPages: Object.entries(pathCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([path, count]) => ({ path, count })),
      averageTimePerPage: Object.entries(totalTimePerPath)
        .map(([path, totalTime]) => ({
          path,
          averageTime: Math.round(totalTime / pathCounts[path]),
        }))
        .sort((a, b) => b.averageTime - a.averageTime)
        .slice(0, 5),
    };
  }, []);

  const clearAnalytics = useCallback(() => {
    analyticsData.current = {
      events: [],
      sessionId: generateSessionId(),
      sessionStart: Date.now(),
    };
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  // Track navigation on location change
  useEffect(() => {
    const currentTime = Date.now();
    const timeSpent = currentTime - entryTime.current;

    // Don't track the initial page load
    if (previousPath.current !== location.pathname) {
      trackNavigation(previousPath.current, location.pathname, timeSpent);
    }

    previousPath.current = location.pathname;
    entryTime.current = currentTime;
  }, [location.pathname, trackNavigation]);

  return {
    getAnalyticsSummary,
    clearAnalytics,
  };
};
